import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { fetchSiteContent, saveSiteContent } from "@/lib/content";
import { api } from "@/lib/api";
import { stripKeys, withKeys } from "./content/shared";
import { SocialsSection, TextSection } from "./content/TextSections";
import { ReviewsSection } from "./content/ReviewsSection";
import { PricesSection, ServingsSection } from "./content/PricingSections";

const KEYED_ROWS = [["testimonials"], ["servings"], ["prices", "mini"], ["prices", "regular"], ["prices", "extras"]];

function getAt(obj, path) {
  return path.reduce((o, k) => o[k], obj);
}

function setAt(obj, path, value) {
  getAt(obj, path.slice(0, -1))[path[path.length - 1]] = value;
}

function decorate(content) {
  const next = structuredClone(content);
  KEYED_ROWS.forEach((path) => setAt(next, path, withKeys(getAt(next, path))));
  return next;
}

function serialize(content) {
  const out = structuredClone(content);
  KEYED_ROWS.forEach((path) => setAt(out, path, stripKeys(getAt(out, path))));
  for (const key of ["flavours", "premium_flavours", "dietary"]) {
    out.prices[key] = out.prices[key].map((s) => s.trim()).filter(Boolean);
  }
  return out;
}

export default function SiteContent() {
  const [content, setContent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    fetchSiteContent().then((c) => setContent(c ? decorate(c) : null));
    api
      .get("/albums")
      .then((r) => setPhotos(r.data.flatMap((a) => a.photos.map((p) => ({ ...p, albumName: a.name })))))
      .catch(() => setPhotos([]));
  }, []);

  if (!content) {
    return (
      <div className="flex justify-center py-24" data-testid="content-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const update = (fn) => {
    const next = structuredClone(content);
    fn(next);
    setContent(next);
  };

  const save = async (section) => {
    setSaving(section);
    try {
      const saved = await saveSiteContent(serialize(content));
      setContent(decorate(saved));
      toast.success("Saved — live on your website now");
    } catch {
      toast.error("Could not save, please try again");
    } finally {
      setSaving("");
    }
  };

  const common = { update, saving, onSave: save };

  return (
    <div className="space-y-6" data-testid="site-content-editor">
      <SocialsSection socials={content.socials} {...common} />
      <TextSection text={content.text} {...common} />
      <ReviewsSection testimonials={content.testimonials} photos={photos} {...common} />
      <PricesSection prices={content.prices} {...common} />
      <ServingsSection servings={content.servings} {...common} />
    </div>
  );
}
